import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  makeStyles,
  createStyles,
  Tab,
  Tabs,
  Theme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useApi } from '@backstage/core-plugin-api';
import { RatingList } from './RatingList';
import { RatingForm } from './RatingForm';
import { UserReview, RatingSubmitData } from './ratingTypes';
import { ratingsApiRef } from '../../../apis/ratingsApi';
import { notificationApiRef } from '../../../apis/notificationApi';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabsRow: {
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: `0 ${theme.spacing(1)}px`,
      flexShrink: 0,
    },
    tabs: {
      flex: 1,
    },
    closeButton: {
      marginRight: theme.spacing(1),
      color: theme.palette.grey[500],
      flexShrink: 0,
    },
    tab: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '14px',
      minWidth: '140px',
      padding: `${theme.spacing(1.5)}px ${theme.spacing(3)}px`,
    },
    dialogContent: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    tabContent: {
      flex: 1,
      overflow: 'auto',
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
    },
    formTabContent: {
      flex: 1,
      overflow: 'auto',
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    pageRoot: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
    },
  }),
);

type ActiveTab = 'view' | 'submit';

interface RatingContainerProps {
  resourceId: string;
  resourceType: string;
  onRatingsLoaded?: (avgRating: number, totalRatings: number) => void;
  onSubmitSuccess?: () => void;
  formTitle?: string;
  showFilters?: boolean;
  /** Render as a Dialog modal (default) or inline page */
  mode?: 'modal' | 'page';
  /** Which tab to open initially: 'view' (default) or 'submit' */
  defaultTab?: 'view' | 'submit';
  /** Required when mode is 'modal' */
  open?: boolean;
  onClose?: () => void;
}

const RatingContainerContent: React.FC<{
  reviews: UserReview[];
  onSubmitRating: (data: RatingSubmitData) => void;
  onSubmitSuccess?: () => void;
  onClose?: () => void;
  formTitle?: string;
  showFilters?: boolean;
  classes: ReturnType<typeof useStyles>;
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab) => void;
}> = ({
  reviews,
  onSubmitRating,
  onSubmitSuccess,
  onClose,
  formTitle,
  showFilters,
  classes,
  activeTab,
  onTabChange,
}) => {
  const handleTabChange = (
    _event: React.ChangeEvent<{}>,
    newValue: ActiveTab,
  ) => {
    onTabChange(newValue);
  };

  return (
    <>
      <Box className={classes.tabsRow}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="View Reviews" value="view" className={classes.tab} />
          <Tab label="Submit Review" value="submit" className={classes.tab} />
        </Tabs>
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            className={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Box className={classes.dialogContent}>
        {activeTab === 'view' && (
          <Box className={classes.tabContent}>
            <RatingList reviews={reviews} showFilters={showFilters} />
          </Box>
        )}
        {activeTab === 'submit' && (
          <Box className={classes.formTabContent}>
            <RatingForm
              formTitle={formTitle}
              onSubmitRating={onSubmitRating}
              onSubmitSuccess={() => {
                onSubmitSuccess?.();
              }}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export const RatingContainer: React.FC<RatingContainerProps> = ({
  resourceId,
  resourceType,
  onRatingsLoaded,
  onSubmitSuccess,
  formTitle,
  showFilters,
  mode = 'modal',
  defaultTab = 'view',
  open = false,
  onClose,
}) => {
  const classes = useStyles();
  const ratingsApi = useApi(ratingsApiRef);
  const notificationApi = useApi(notificationApiRef);
  const [activeTab, setActiveTab] = useState<ActiveTab>(defaultTab);
  const [userRatings, setUserRatings] = useState<UserReview[]>([]);

  const loadRatings = () => {
    if (!resourceId) return;
    ratingsApi
      .getRatings(resourceId, resourceType)
      .then((data: any) => {
        const payload = data?.data ?? data;
        const ratings = Array.isArray(payload?.ratings) ? payload.ratings : [];
        setUserRatings(
          ratings.map((r: any) => ({
            rating: r.rating,
            comment: r.comment,
            userEmail: r.userEmail,
            apmNumber: r.apmNumber,
            timestamp: r.ratedAt ? new Date(r.ratedAt).getTime() : undefined,
          })),
        );
        if (onRatingsLoaded) {
          onRatingsLoaded(
            payload?.avgRating ?? 0,
            payload?.totalRatings ?? ratings.length,
          );
        }
      })
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error('getRatings failed:', err);
      });
  };

  const handleSubmit = ({ rating, comment, apmNumber }: RatingSubmitData) => {
    if (!resourceId) return;
    ratingsApi
      .submitRating({ resourceId, resourceType, rating, comment, apmNumber })
      .then(() => {
        loadRatings();
      })
      .catch(() => {
        notificationApi.sendNotification({
          severity: 'error',
          disapperAfterMs: 2000,
          message: 'Failed to submit rating. Please try again.',
        });
      });
  };

  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
      loadRatings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (mode === 'page') {
      loadRatings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab);
  };

  const sharedContent = (
    <RatingContainerContent
      reviews={userRatings}
      onSubmitRating={handleSubmit}
      onSubmitSuccess={onSubmitSuccess}
      onClose={mode === 'modal' ? onClose : undefined}
      formTitle={formTitle}
      showFilters={showFilters}
      classes={classes}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    />
  );

  if (mode === 'page') {
    return <Box className={classes.pageRoot}>{sharedContent}</Box>;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          height: '512px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {sharedContent}
    </Dialog>
  );
};
